import * as React from 'react'

import {
  CONTENT_WRAPPER_ID,
  EDocumentComparisonChangeType,
  ETargetVersion,
} from '../../constants'
import { TDiffMarker } from '../../types'
import { IHTMLCompareChangeItem } from '../types'

/**
 * Options for configuring the minimap markers hook
 */
type UseHtmlMinimapMarkersOptions = {
  compareDocumentDiff: IHTMLCompareChangeItem[] | null // Diff between versions
  sourceVersionId: string | null // ID of source version element
  targetVersionId: string | null // ID of target version element
  isEnabled: boolean // Whether this hook is active
}

/**
 * Custom hook for generating minimap markers for HTML version comparison
 * Creates visual indicators of changes on a minimap/scrollbar preview
 * Calculates positions relative to scroll height for proper minimap placement
 *
 * @param options - Configuration options
 * @returns Object containing diffMarkers array and refreshMarkers function
 */
export const useMinimapMarkers = ({
  compareDocumentDiff,
  sourceVersionId,
  targetVersionId,
  isEnabled,
}: UseHtmlMinimapMarkersOptions) => {
  const [diffMarkers, setDiffMarkers] = React.useState<{
    sourceMarkers: TDiffMarker[]
    targetMarkers: TDiffMarker[]
  } | null>(null)

  const getShadowRoot = React.useCallback((versionId: string | null) => {
    if (!versionId) return null
    const scrollContainer = document.getElementById(versionId)
    if (!scrollContainer) return null
    const shadowHost = scrollContainer.querySelector(`#${CONTENT_WRAPPER_ID}`)
    return shadowHost?.shadowRoot ?? null
  }, [])

  const buildMarkersForVersion = React.useCallback(
    (params: {
      versionId: string | null // Version ID to build markers for
      changes: IHTMLCompareChangeItem[] // All changes to filter
      column: ETargetVersion // Column identifier (SOURCE or TARGET)
      types: EDocumentComparisonChangeType[] // Types of changes to include for this column
    }) => {
      const { versionId, changes, column, types } = params
      if (!versionId) return []

      const scrollContainer = document.getElementById(versionId)
      const shadowRoot = getShadowRoot(versionId)
      if (!scrollContainer || !shadowRoot) return []

      const scrollHeight = scrollContainer.scrollHeight
      if (scrollHeight <= 0) return []

      // Convert element positions into scroll-height percentages for minimap.
      const hostRect = shadowRoot.host.getBoundingClientRect()
      const containerRect = scrollContainer.getBoundingClientRect()

      return changes
        .filter((change) => types.includes(change.type))
        .map((change, index) => {
          const element = shadowRoot.querySelector(change.path)
          if (!element) return null

          // Calculate element position relative to scroll container
          const elementRect = element.getBoundingClientRect()
          const elementTopRelativeToHost = elementRect.top - hostRect.top
          const hostTopRelativeToContainer =
            hostRect.top - containerRect.top + scrollContainer.scrollTop
          const absoluteTop =
            hostTopRelativeToContainer + elementTopRelativeToHost

          // Convert to percentages for minimap (0-100%)
          const topPercent = (absoluteTop / scrollHeight) * 100
          const heightPercent = (elementRect.height / scrollHeight) * 100

          return {
            key: change.uuid ?? `${column}-${index}`,
            column,
            topPercent,
            heightPercent: Math.max(heightPercent, 0.4), // Minimum height for visibility
            type: change.type,
          }
        })
        .filter(
          (marker): marker is NonNullable<typeof marker> => marker !== null
        )
    },
    [getShadowRoot]
  )

  const refreshMarkers = React.useCallback(() => {
    if (
      !isEnabled ||
      !compareDocumentDiff ||
      compareDocumentDiff.length === 0
    ) {
      setDiffMarkers(null)
      return
    }

    // Source shows delete/replace, target shows insert/replace.
    const sourceMarkers = buildMarkersForVersion({
      versionId: sourceVersionId,
      changes: compareDocumentDiff,
      column: ETargetVersion.SOURCE,
      types: [
        EDocumentComparisonChangeType.DELETE,
        EDocumentComparisonChangeType.REPLACE,
      ],
    })

    const targetMarkers = buildMarkersForVersion({
      versionId: targetVersionId,
      changes: compareDocumentDiff,
      column: ETargetVersion.TARGET,
      types: [
        EDocumentComparisonChangeType.INSERT,
        EDocumentComparisonChangeType.REPLACE,
      ],
    })

    setDiffMarkers({ sourceMarkers, targetMarkers })
  }, [
    buildMarkersForVersion,
    compareDocumentDiff,
    isEnabled,
    sourceVersionId,
    targetVersionId,
  ])

  /**
   * Effect: Refresh markers when dependencies change
   */
  React.useEffect(() => refreshMarkers(), [refreshMarkers])

  return React.useMemo(
    () => ({ diffMarkers, refreshMarkers }),
    [diffMarkers, refreshMarkers]
  )
}
